"use client";

import { CACHE_NAMES, DEBUG_CHANNEL, DEBUG_MAX_EVENTS, type SwFetchEvent } from "./cache-constants";

/**
 * Client-side fetch/cache telemetry for the Debug tab.
 *
 * Two event sources feed a single ring buffer:
 *  - App code (TripDataProvider, trips page) records its own cache-first
 *    loads, version checks, and image prefetches via `recordDebugEvent`.
 *  - The service worker broadcasts every request it handles (page
 *    navigations, API calls, static assets) on the "trip-debug"
 *    BroadcastChannel, tagged with cache name + hit/miss + duration.
 *
 * The buffer is persisted (throttled) to the "debug-log" cache so the log
 * survives reloads. Sync Now clears all caches, which also resets the log.
 */

export interface DebugEvent {
  /** Unique key. App events: "app-<n>". SW events: "sw-<session>-<seq>". */
  id: string;
  /** Epoch ms when the request completed. */
  ts: number;
  origin: "app" | "sw";
  /** What kind of call: trip-data | version-check | image | trips-list | page | api | auth | static | sync | other */
  kind: string;
  url: string;
  /**
   * Where the response came from:
   * "cache:<name>" | "network" | "network→cache:<name>" (fetched live, then
   * stored) | "cache-fallback:<name>" (network failed, served stale) |
   * "offline" (expected miss while offline) | "error"
   */
  source: string;
  durationMs?: number;
  status?: number;
  /** Response body size in bytes (wire size when Content-Length was present,
   * otherwise the decoded body size). Used by the Debug panel to total what
   * the app actually pulls over the network vs serves from cache. */
  bytes?: number;
  detail?: string;
}

const MAX_EVENTS = DEBUG_MAX_EVENTS;
const DEBUG_CACHE = CACHE_NAMES.debugLog;
const DEBUG_LOG_KEY = "/_debug/fetch-log";

let events: DebugEvent[] = [];
let snapshot: DebugEvent[] = [];
let appSeq = 0;
const seenIds = new Set<string>();
const listeners = new Set<() => void>();
let initialized = false;
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function notify() {
  snapshot = [...events];
  for (const l of listeners) l();
}

function schedulePersist() {
  if (persistTimer) return;
  persistTimer = setTimeout(async () => {
    persistTimer = null;
    try {
      const cache = await caches.open(DEBUG_CACHE);
      await cache.put(
        new Request(DEBUG_LOG_KEY),
        new Response(JSON.stringify(events), { headers: { "Content-Type": "application/json" } }),
      );
    } catch { /* cache unavailable — log stays in-memory */ }
  }, 2000);
}

function push(evt: DebugEvent) {
  if (seenIds.has(evt.id)) return;
  seenIds.add(evt.id);
  events.push(evt);
  if (events.length > MAX_EVENTS) {
    const dropped = events.splice(0, events.length - MAX_EVENTS);
    for (const d of dropped) seenIds.delete(d.id);
  }
  notify();
  schedulePersist();
}

function kindForCacheName(cacheName: string, url: string): string {
  if (cacheName === CACHE_NAMES.pages) return "page";
  if (cacheName === CACHE_NAMES.authSession) return "auth";
  if (cacheName === CACHE_NAMES.tripData) return url.includes("type=version") ? "version-check" : "api";
  if (cacheName === "version-check") return "version-check";
  if (cacheName === CACHE_NAMES.nextStatic) return "static";
  if (cacheName.includes("rsc-prefetch") || url.includes("_rsc=")) return "prefetch";
  if (cacheName.includes("rsc")) return "page-rsc";
  if (url.includes("/_next/")) return "static";
  return "other";
}

function ingestSwEvent(e: SwFetchEvent, live = false) {
  // A live "error" while this page is offline is an expected miss (router
  // prefetches, manifest re-fetches) — label it so, matching the SW's own
  // onLine check. Replayed dumps keep their recorded source: the online
  // state at fetch time is unknown by the time they're ingested.
  const source =
    live && e.source === "error" && typeof navigator !== "undefined" && !navigator.onLine
      ? "offline"
      : e.source;
  push({
    id: `sw-${e.session}-${e.seq}`,
    ts: e.ts,
    origin: "sw",
    kind: kindForCacheName(e.cacheName, e.url),
    url: e.url,
    source,
    durationMs: e.durationMs,
    status: e.status,
    bytes: e.bytes,
    detail: `${e.strategy} · cache "${e.cacheName}"`,
  });
}

function ensureInit() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  // Restore persisted log (async — UI updates when it lands)
  (async () => {
    try {
      const cache = await caches.open(DEBUG_CACHE);
      const res = await cache.match(DEBUG_LOG_KEY);
      if (!res) return;
      const persisted: DebugEvent[] = await res.json();
      if (!Array.isArray(persisted)) return;
      const merged = [...persisted.filter((e) => !seenIds.has(e.id)), ...events];
      merged.sort((a, b) => a.ts - b.ts);
      events = merged.slice(-MAX_EVENTS);
      seenIds.clear();
      for (const e of events) seenIds.add(e.id);
      notify();
    } catch { /* no persisted log */ }
  })();

  // Live SW events + log dumps
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(DEBUG_CHANNEL);
    channel.onmessage = (msg) => {
      const data = msg.data;
      if (data?.type === "sw-fetch") {
        ingestSwEvent(data.event as SwFetchEvent, true);
      } else if (data?.type === "sw-log-dump" && Array.isArray(data.events)) {
        for (const e of data.events as SwFetchEvent[]) ingestSwEvent(e);
      }
    };
  }
}

/** Record an app-originated fetch event. */
export function recordDebugEvent(evt: Omit<DebugEvent, "id" | "ts" | "origin"> & { ts?: number }) {
  ensureInit();
  push({ id: `app-${++appSeq}-${Math.random().toString(36).slice(2, 7)}`, ts: evt.ts ?? Date.now(), origin: "app", ...evt });
}

/** Ask the SW for its buffered event log (covers requests made before this page subscribed). */
export function requestSwDebugLog() {
  ensureInit();
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: "TRIP_DEBUG_LOG_REQUEST" });
  } catch { /* no SW */ }
}

export function subscribeDebugEvents(listener: () => void): () => void {
  ensureInit();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Stable snapshot for useSyncExternalStore. */
export function getDebugEvents(): DebugEvent[] {
  return snapshot;
}

export async function clearDebugLog() {
  events = [];
  seenIds.clear();
  notify();
  // Clear the SW's in-worker ring buffer too — it re-dumps on every Debug
  // panel mount, so without this every cleared event reappeared immediately
  // ("clearing doesn't persist").
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: "TRIP_DEBUG_LOG_CLEAR" });
  } catch { /* no SW */ }
  try {
    const cache = await caches.open(DEBUG_CACHE);
    await cache.delete(DEBUG_LOG_KEY);
  } catch { /* ignore */ }
}
